import { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, FormControl, InputLabel, MenuItem, Select, FormLabel, FormControlLabel, Radio, Tooltip, } from "@material-ui/core";
import RadioGroup from "@material-ui/core/RadioGroup";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import AttachmentIcon from "@material-ui/icons/Attachment";
import LinkIcon from "@material-ui/icons/Link";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import { useSnackbar } from "notistack";
import { backendDateFormat, dayMonthDateFormat, fieldDMYShort, assetType as assetTypeMap, imgStoragePath } from "../../../../constants";
import { regexConstants } from "../../../../constants/regEx";
import { globalGetService, globalPostService, globalPutService, globalDeleteService, globalFileUploadService, } from "../../../../utils/globalApiServices";
import { checkApiStatus } from "../../../../utils";
import { trackActivity } from "../../../../utils/mixpanel";
import { acumenCamo } from "../../../../shared_elements/components";
import LinkFiles from "../../../../shared_elements/components/LinkFiles";
import UploadFile from "../../../../shared_elements/components/UploadFile";
import { isMobile } from "react-device-detect";
import config from "../../../../config";

const defaultFormValues = {
	ata_chapter: null,
	part_description: null,
	part_number: null,
	part_serial_number: null,
	part_installation_date: null,
	crs_date: null,
	aircraft_tsn_installation: null,
	aircraft_csn_installation: null,
	part_tsn_installation: null,
	part_csn_installation: null,
	ht_requirement: null,
	dimension_type: "D",
	dimension_interval: null,
	dimension_interval_days: null,
	dimension_interval_fc: null,
	remark: null,
	task_card: null,
	component: null,
	position: null,
	tso: null,
	cso: null,
	faa_easa: null,
	shop_report: null,
	conformity_crt: null,
	goods_rec_note: null,
	installation_wo: null,
	checklist: [],
	folder_uuid: null,
};

const buildValidationInputs = (data) => {
	let inputs = {
		ata_chapter: (acumenCamo || data?.ata_chapter?.trim()) ? '' : "Please enter ATA chapter",
		part_number: data?.part_number?.trim() ? '' : "Please enter part number",
		part_serial_number: (acumenCamo || data?.part_serial_number?.trim()) ? '' : "Please enter part serial number",
		ht_requirement: (acumenCamo || data?.ht_requirement?.trim()) ? '' : "Please enter ht requirement",
		task_card: (acumenCamo || data?.task_card?.trim()) ? '' : "Please enter task card",
		component: (acumenCamo || data?.component?.trim()) ? '' : "Please enter component",
		position: (acumenCamo || data?.position?.trim()) ? '' : "Please enter position",
		faa_easa: (acumenCamo || data?.faa_easa) ? '' : "Please enter FAA EASA",
		installation_wo: (acumenCamo || data?.installation_wo) ? '' : "Please enter installation",
		part_description: (acumenCamo || data?.part_description?.trim()) ? '' : "Please enter part description",
		part_installation_date: (acumenCamo || data?.part_installation_date) ? '' : "Please select part instalation date",
		crs_date: (acumenCamo || data?.crs_date) ? '' : "Please select CRS date",
	};
	if (data.dimension_type === "FC") {
		inputs = { ...inputs, cso: (acumenCamo || data?.cso) ? '' : "Please enter cso", dimension_interval_fc: (acumenCamo || data?.dimension_interval_fc) ? '' : "Please enter fc" };
	} else if (data.dimension_type === "FC,D") {
		inputs = { ...inputs, tso: (acumenCamo || data?.tso) ? '' : "Please enter tso", cso: (acumenCamo || data?.cso) ? '' : "Please enter cso", dimension_interval_fc: (acumenCamo || data?.dimension_interval_fc) ? '' : "Please enter fc", dimension_interval_days: (acumenCamo || data?.dimension_interval_days) ? '' : "Please enter days" };
	} else if (data.dimension_type === "FH") {
		inputs = { ...inputs, tso: (acumenCamo || data?.tso) ? '' : "Please enter tso", dimension_interval: (acumenCamo || data?.dimension_interval) ? '' : "Please enter fh" };
	} else if (data.dimension_type === "D") {
		inputs = { ...inputs, tso: (acumenCamo || data?.tso) ? '' : "Please enter tso", dimension_interval_days: (acumenCamo || data?.dimension_interval_days) ? '' : "Please enter days" };
	} else if (data.dimension_type === "FH,D") {
		inputs = { ...inputs, tso: (acumenCamo || data?.tso) ? '' : "Please enter tso", dimension_interval: (acumenCamo || data?.dimension_interval) ? '' : "Please enter fh", dimension_interval_days: (acumenCamo || data?.dimension_interval_days) ? '' : "Please enter days" };
	} else if (data.dimension_type === "FC,FH") {
		inputs = { ...inputs, tso: (acumenCamo || data?.tso) ? '' : "Please enter tso", cso: (acumenCamo || data?.cso) ? '' : "Please enter cso", dimension_interval_fc: (acumenCamo || data?.dimension_interval_fc) ? '' : "Please enter fc", dimension_interval: (acumenCamo || data?.dimension_interval) ? '' : "Please enter fh" };
	} else if (data.dimension_type === "FC,FH,D") {
		inputs = { ...inputs, tso: (acumenCamo || data?.tso) ? '' : "Please enter tso", cso: (acumenCamo || data?.cso) ? '' : "Please enter cso", dimension_interval_fc: (acumenCamo || data?.dimension_interval_fc) ? '' : "Please enter fc", dimension_interval: (acumenCamo || data?.dimension_interval) ? '' : "Please enter fh", dimension_interval_days: (acumenCamo || data?.dimension_interval_days) ? '' : "Please enter days" };
	}
	return inputs;
};

const safeDecimal = (val) => Number.isInteger(val) ? val : parseFloat(val?.toFixed(2));
const isValidNum = (val) => val !== null && val !== undefined && val !== '' && !isNaN(Number(val));
const isValidDate = (date) => !!date && moment(date).isValid();

const computeRemainingVal = (data, headerTsn, headerCsn) => {
	const today = moment();
	const targetDate = moment(data.crs_date);
	const todayDate = new Date();
	if (data.dimension_type === "D") {
		if (!isValidNum(data.dimension_interval_days) || !isValidDate(data.crs_date)) return null;
		return data.dimension_interval_days - today.diff(targetDate, "days");
	} else if (data.dimension_type === "FC") {
		if (!isValidNum(data.dimension_interval_fc) || !isValidNum(data.cso)) return null;
		return safeDecimal(data.dimension_interval_fc - data.cso);
	} else if (data.dimension_type === "FH") {
		if (!isValidNum(data.dimension_interval) || !isValidNum(data.tso)) return null;
		return safeDecimal(data.dimension_interval - data.tso);
	} else if (data.dimension_type === "FC,D") {
		if (!isValidNum(data.dimension_interval) || !isValidNum(data.cso) || !isValidNum(data.dimension_interval_fc) || !isValidDate(data.crs_date)) return null;
		const fcRemaining = safeDecimal(data.dimension_interval - data.cso);
		const dRemaining = data.dimension_interval_fc - today.diff(targetDate, "days");
		const fcDueAt = safeDecimal(+fcRemaining + +headerCsn);
		const finalFcDue = moment(todayDate).add(fcDueAt, "days");
		const finalDDue = moment(todayDate).add(dRemaining, "days");
		return finalDDue < finalFcDue ? dRemaining : fcRemaining;
	} else if (data.dimension_type === "FH,D" || data.dimension_type === "FC,FH,D" || data.dimension_type === "FC,FH") {
		if (!isValidNum(data.dimension_interval) || !isValidNum(data.tso) || !isValidNum(data.dimension_interval_days) || !isValidDate(data.crs_date)) return null;
		const fhRemaining = safeDecimal(data.dimension_interval - data.tso);
		const dRemaining = data.dimension_interval_days - today.diff(targetDate, "days");
		const fhDueAt = safeDecimal(+fhRemaining + +headerTsn);
		const finalFhDue = moment(todayDate).add(fhDueAt, "days");
		const finalDDue = moment(todayDate).add(dRemaining, "days");
		return finalDDue < finalFhDue ? dRemaining : fhRemaining;
	}
	return null;
};

const computeNextDueVal = (data, headerTsn, headerCsn) => {
	const remainingValue = computeRemainingVal(data, headerTsn, headerCsn);
	if (remainingValue === null || remainingValue === undefined || isNaN(remainingValue)) return null;
	const todayDate = new Date();
	const today = moment();
	const targetDate = moment(data.crs_date);
	if (data.dimension_type === "D") {
		return moment(todayDate).add(remainingValue, "days").format("DD-MM-YYYY");
	} else if (data.dimension_type === "FH") {
		if (!isValidNum(headerTsn)) return null;
		return safeDecimal(+remainingValue + +headerTsn);
	} else if (data.dimension_type === "FC") {
		if (!isValidNum(headerCsn)) return null;
		return safeDecimal(+remainingValue + +headerCsn);
	} else if (data.dimension_type === "FC,D") {
		if (!isValidNum(data.dimension_interval) || !isValidNum(data.cso) || !isValidNum(data.dimension_interval_days) || !isValidDate(data.crs_date) || !isValidNum(headerCsn)) return null;
		const fcRemaining = safeDecimal(data.dimension_interval - data.cso);
		const dRemaining = data.dimension_interval_days - today.diff(targetDate, "days");
		const fcDueAt = safeDecimal(+fcRemaining + +headerCsn);
		const finalFcDue = moment(todayDate).add(fcDueAt, "days");
		const finalDDue = moment(todayDate).add(dRemaining, "days");
		return finalDDue < finalFcDue ? moment(finalDDue).format("DD-MM-YYYY") : fcDueAt;
	} else if (data.dimension_type === "FH,D" || data.dimension_type === "FC,FH,D") {
		if (!isValidNum(data.dimension_interval) || !isValidNum(data.tso) || !isValidNum(data.dimension_interval_days) || !isValidDate(data.crs_date) || !isValidNum(headerTsn)) return null;
		const fhRemaining = safeDecimal(data.dimension_interval - data.tso);
		const dRemaining = data.dimension_interval_days - today.diff(targetDate, "days");
		const fhDueAt = safeDecimal(+fhRemaining + +headerTsn);
		const finalFhDue = moment(todayDate).add(fhDueAt, "days");
		const finalDDue = moment(todayDate).add(dRemaining, "days");
		return finalDDue < finalFhDue ? moment(finalDDue).format("DD-MM-YYYY") : fhDueAt;
	}
	return null;
};

const HtTaskForm = ({ match, modeType, htData, modalCloseFn, getResponseBack, headerTsn, headerCsn, uuid, last_used_folder_uuid, last_used_folder_uuid_link_document, checkbox_last_used_folder_uuid, checkbox_last_used_folder_name, }) => {
	const { enqueueSnackbar } = useSnackbar();
	const [error, setError] = useState({});
	const [data, setData] = useState(defaultFormValues);
	const [attachments, setAttachments] = useState([]);
	const [newUploadedAttachments, setNewUploadedAttachments] = useState([]);
	const [linkFile, setLinkFile] = useState(false);
	const [uploadFile, setUploadFile] = useState(false);

	const addAnotherRef = useRef(false);
	const newUploadedAttachmentsRef = useRef(newUploadedAttachments);
	newUploadedAttachmentsRef.current = newUploadedAttachments;

	useEffect(() => {
		if (modeType === 'edit' && htData) {
			globalGetService(`camo/ht/view/${htData.id}/`).then((response) => {
				if (checkApiStatus(response)) {
					const d = response.data.data;
					setData(d);
					setAttachments(d.attachments || []);
				}
			});
		}
		trackActivity('CAMO', { event_type: 'Modal Opened', page_title: 'Hard Time Add Task Form' });
	}, []);

	const onFieldChange = (key, value) => setData(prev => ({ ...prev, [key]: value }));
	const onRestErrorKey = (key) => setError(prev => ({ ...prev, [key]: "" }));

	const buildUploadFormData = (uploadId) => {
		const formdata = new FormData();
		newUploadedAttachmentsRef.current.forEach((file, key) => {
			formdata.append(`[${key}]file`, file);
			formdata.append(`[${key}]file_type`, file.type);
			formdata.append(`[${key}]file_name`, file.name);
			formdata.append(`[${key}]module_id`, uploadId);
			formdata.append(`[${key}]module_type_id`, 2);
			formdata.append(`[${key}]asset_id`, match.params.asset);
			formdata.append(`[${key}]asset_type_id`, match.params.assetType);
		});
		return formdata;
	};

	const onSubmitFileLink = async (id) => {
		const checklist = data?.checklist;
		if (checklist?.length > 0 && checklist.map(item => item.id).filter(Boolean).length > 0) {
			let payload = {};
			const hasFolders = checklist.some(item => item.type === 'folder');
			const hasFiles = checklist.some(item => item.type === 'file');
			if (hasFolders && hasFiles) {
				payload.folder_ids = checklist.filter(item => item.type === 'folder').map(f => f.id).join(',');
				payload.file_ids = checklist.filter(item => item.type === 'file').map(f => f.id).join(',');
			} else if (hasFolders) {
				payload.folder_ids = checklist.filter(item => item.type === 'folder').map(f => f.id).join(',');
			} else {
				payload.file_ids = checklist.filter(item => item.type === 'file').map(f => f.id).join(',');
			}
			await globalPutService(`camo/ht/${id}/document-checklists/`, payload).then(response => {
				if (!checkApiStatus(response)) {
					enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
				}
			});
		}
		if (data?.folder_uuid?.length > 0) {
			const formData = new FormData();
			formData.append('folder_uuid', data.folder_uuid);
			data.checklist.filter(item => item instanceof File).forEach((file) => formData.append('files[]', file));
			await globalPutService(`camo/ht/${id}/document-checklists/`, formData).then(response => {
				if (!checkApiStatus(response)) {
					enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
				}
			});
		}
	};

	const handleFormSubmit = async (e) => {
		e.preventDefault();
		const isAddAnother = addAnotherRef.current;
		const validationInputs = buildValidationInputs(data);
		if (!Object.keys(validationInputs).every(k => validationInputs[k] === "")) {
			setError(validationInputs);
			trackActivity('CAMO', { event_type: modeType === "add" ? 'Add TASK Failed' : 'Update TASK Failed', page_title: 'Hard Time(HT)', error_source: 'Frontend', error_type: validationInputs });
			return;
		}
		let payload = { ...data };
		payload = {
			...payload,
			asset_id: match.params.asset,
			asset_type_id: match.params.assetType,
			dimension_interval: payload?.dimension_interval || null,
			dimension_interval_days: payload?.dimension_interval_days || null,
			dimension_interval_fc: payload?.dimension_interval_fc || null,
			aircraft_tsn_installation: payload?.aircraft_tsn_installation || null,
			aircraft_csn_installation: payload?.aircraft_csn_installation || null,
			tso: payload?.tso || null,
			cso: payload?.cso || null,
			part_tsn_installation: payload?.part_tsn_installation || null,
			part_csn_installation: payload?.part_csn_installation || null,
		};
		delete payload.ad_due;
		delete payload.remaining_val;
		delete payload.ht_DUE;

		if (modeType === 'add') {
			if (payload.dimension_type === "FC") payload.tso = null;
			else if (payload.dimension_type === "FH") payload.cso = null;
			const response = await globalPostService('camo/ht/create/', payload);
			if (checkApiStatus(response)) {
				const uploadId = response.data.data.id;
				if (newUploadedAttachmentsRef.current.length > 0) {
					await globalFileUploadService('camo/attachment/upload/', buildUploadFormData(uploadId));
				}
				await onSubmitFileLink(uploadId);
				trackActivity('CAMO', { event_type: 'Task Added Successfully', page_title: 'Hard Time(HT)', item_id: uploadId || '', request_body: payload || '', response_msg: response.data.message || '' });
				enqueueSnackbar("Data Submitted Successfully", { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" } });
				getResponseBack();
				if (isAddAnother) {
					setData(defaultFormValues);
					setAttachments([]);
					setNewUploadedAttachments([]);
					document.getElementById("asset-form").reset();
				} else {
					modalCloseFn();
				}
			} else {
				enqueueSnackbar(response?.data?.message || "Something went wrong.", { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
			}
		} else {
			if (newUploadedAttachmentsRef.current.length > 0) {
				await globalFileUploadService('camo/attachment/upload/', buildUploadFormData(htData.id));
			}
			const response = await globalPutService(`camo/ht/${htData.id}/update/`, payload);
			if (checkApiStatus(response)) {
				enqueueSnackbar("Record Updated Successfully", { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" } });
				trackActivity('CAMO', { event_type: 'Task Updated Successfully', page_title: 'Hard Time(HT)', item_id: htData.id || '', request_body: payload || '' });
				getResponseBack();
				modalCloseFn();
			} else {
				enqueueSnackbar(response?.data?.message || "Something went wrong.", { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
			}
		}
	};

	const onUnlinkCheckListFile = (checklist, fileId) => {
		const payload = {
			delete: true,
			folder_ids: checklist?.folders?.filter(item => item.type === 'folder').map(f => f.id).filter(id => id !== fileId).join(','),
			file_ids: checklist?.files?.filter(item => item.type === 'file').map(f => f.id).filter(id => id !== fileId).join(','),
		};
		globalPutService(`camo/ht/${htData.id}/document-checklists/`, payload).then((response) => {
			if (checkApiStatus(response)) {
				setData(prev => ({
					...prev,
					checklist: {
						...prev.checklist,
						files: checklist.files.filter(f => f.id !== fileId),
						folders: checklist?.folders?.filter(f => f.id !== fileId),
					},
				}));
				trackActivity('CAMO', { event_type: 'Checklist File Unlinked', page_title: 'Hard Time(HT)', file_id: fileId || '', edited_form_id: htData.id || '' });
			}
		});
	};

	const deleteAttachmentFile = (id, file) => {
		if (id !== undefined && id !== 0) {
			globalDeleteService(`camo/attachment/${id}/delete/`).then((response) => {
				if (checkApiStatus(response)) {
					enqueueSnackbar("Attachment Deleted Successfully", { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" } });
					trackActivity("CAMO", { event_type: "Attachment Deleted", page_title: "Hard Time(HT)", item_id: id });
					setAttachments(prev => prev.filter(item => item.id !== id));
				} else {
					enqueueSnackbar("Something went wrong.", { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
				}
			});
		} else {
			setAttachments(prev => prev.filter(item => item.file_name !== file.file_name));
		}
	};

	const onUpload = (files) => {
		const chosenFiles = Array.prototype.slice.call(files);
		const valid = [];
		chosenFiles.forEach(f => {
			if (f.size > 52428800) {
				enqueueSnackbar(`${f.name} Maximum file size should be 50MB.`, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
			} else {
				valid.push(f);
			}
		});
		if (valid.length > 0) {
			setNewUploadedAttachments(prev => [...prev, ...valid]);
			setAttachments(prev => [...prev, ...valid.map(file => ({
				id: 0,
				module_id: 0,
				module_type_id: 2,
				file_name: file.name,
				file_type: file.type,
				file: "",
				name: file.name,
				asset_id: match.params.asset,
				asset_type_id: match.params.assetType,
			}))]);
		}
	};

	const onRemoveFile = (file) => setData(prev => ({ ...prev, checklist: prev.checklist.filter(item => item.name !== file.name) }));

	const getResponseBackFn = (file) => setData(prev => ({ ...prev, checklist: file }));

	const listResponseBack = (file) => { if (modeType !== "add") getResponseBack(file); };

	const onLinkFile = (file, uuidVal, type) => {
		if (type === "link" || uuidVal) {
			setData(prev => ({
				...prev,
				checklist: [...prev.checklist, file].flat(),
				folder_uuid: [...(prev.folder_uuid || []), uuidVal].flat().join(""),
			}));
		} else {
			onUpload(file);
		}
	};

	const addFormData = (e) => { addAnotherRef.current = false; handleFormSubmit(e); };
	const saveAndAddAnother = (e) => { addAnotherRef.current = true; handleFormSubmit(e); };

	const dimensionLabel = (type) => {
		const labels = { D: "Days", FC: "FC", FH: "FH", "FH,D": "FH/Days", "FC,D": "FC/Days", "FC,FH,D": "FC/FH/Days", "FC,FH": "FC/FH" };
		return labels[type] || "Days";
	};

	return (
		<>
			<Dialog open={true} maxWidth="lg" fullWidth>
				<div id="HtTaskForm" className="ht-form">
					<DialogTitle className="ht-form__title">
						{modeType === 'add' ? "Add HT Component" : "Update HT Component"}
						<CloseIcon onClick={modalCloseFn} className="ht-form__close-icon" />
					</DialogTitle>
					<form id="asset-form" onSubmit={handleFormSubmit}>
						<DialogContent>
							<Grid spacing={1} container>
								<Grid item md={3} sm={6} xs={12}>
									<TextField
										required={acumenCamo ? false : true}
										variant="outlined"
										label="ATA Chapter"
										name="ata_chapter"
										id="ata_chapter"
										value={data.ata_chapter || ''}
										error={!!error.ata_chapter}
										helperText={acumenCamo ? '' : error.ata_chapter || ""}
										onChange={(e) => {
											if (e.target.value.length <= 20 && (regexConstants.numericWithHyphen.test(e.target.value) || e.target.value === "")) {
												onFieldChange('ata_chapter', e.target.value);
												onRestErrorKey("ata_chapter");
											}
										}}
										onFocus={() => onRestErrorKey("ata_chapter")}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<TextField
										required
										variant="outlined"
										label="Part Number"
										name="part_number"
										id="part_number"
										value={data?.part_number || ''}
										error={!!error.part_number}
										helperText={error.part_number || ""}
										onChange={(e) => { onFieldChange("part_number", e.target.value.slice(0, 20)); onRestErrorKey("part_number"); }}
										InputLabelProps={{ shrink: true }}
										onFocus={() => onRestErrorKey("part_number")}
									/>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<TextField
										required={acumenCamo ? false : true}
										variant="outlined"
										label="Part Serial Number"
										name="part_serial_number"
										id="part_serial_number"
										value={data.part_serial_number || ''}
										onChange={(e) => { onFieldChange("part_serial_number", e.target.value.slice(0, 20)); onRestErrorKey("part_serial_number"); }}
										InputLabelProps={{ shrink: true }}
										error={!!error.part_serial_number}
										helperText={error.part_serial_number || ""}
										onFocus={() => onRestErrorKey("part_serial_number")}
									/>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<TextField
										variant="outlined"
										required={acumenCamo ? false : true}
										label="Task Card"
										name="task_card"
										id="task_card"
										value={data.task_card || ''}
										InputLabelProps={{ shrink: true }}
										onChange={(e) => { onFieldChange("task_card", e.target.value.slice(0, 20)); onRestErrorKey("task_card"); }}
										error={!!error.task_card}
										helperText={error.task_card || ""}
										onFocus={() => onRestErrorKey("task_card")}
									/>
								</Grid>
								<Grid item md={12} sm={12} xs={12}>
									<TextField
										required={acumenCamo ? false : true}
										variant="outlined"
										label="Part Description"
										name="part_description"
										id="part_description"
										multiline
										InputLabelProps={{ shrink: true }}
										value={data?.part_description || ''}
										error={!!error.part_description}
										helperText={acumenCamo ? '' : error.part_description || ""}
										onChange={(e) => { onFieldChange("part_description", e.target.value.slice(0, 255)); onRestErrorKey("part_description"); }}
										onFocus={() => onRestErrorKey("part_description")}
									/>
								</Grid>

								<Grid item md={3} sm={6} xs={12}>
									<TextField
										required={acumenCamo ? false : true}
										variant="outlined"
										label="Component"
										name="component"
										id="component"
										value={data.component || ''}
										onChange={(e) => { onFieldChange("component", e.target.value.slice(0, 20)); onRestErrorKey("component"); }}
										InputLabelProps={{ shrink: true }}
										error={!!error.component}
										helperText={error.component || ""}
										onFocus={() => onRestErrorKey("component")}
									/>
								</Grid>
								<Grid item md={3} sm={6} xs={12} className="input-calender">
									<MuiPickersUtilsProvider utils={MomentUtils}>
										<KeyboardDatePicker
											required={acumenCamo ? false : true}
											id="part_installation_date"
											label="Part Installation Date"
											format={acumenCamo ? fieldDMYShort : dayMonthDateFormat}
											inputVariant="outlined"
											fullWidth
											disableFuture
											InputLabelProps={{ shrink: true }}
											minDate={moment().subtract(25, "years")}
											value={data.part_installation_date}
											error={!!error.part_installation_date}
											helperText={error.part_installation_date || ""}
											onChange={(date) => { onFieldChange("part_installation_date", moment(date).isValid ? moment(date).format(backendDateFormat) : null); onRestErrorKey("part_installation_date"); }}
										/>
									</MuiPickersUtilsProvider>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<TextField
										variant="outlined"
										label="Part TSN Installation"
										name="part_tsn_installation"
										id="part_tsn_installation"
										value={data.part_tsn_installation || ''}
										InputLabelProps={{ shrink: true }}
										onChange={(e) => {
											const input = e.target.value;
											if (input.length <= 10 && regexConstants.onlyNumeric.test(input)) { onFieldChange("part_tsn_installation", input); }
											else if (input.length > 10 && regexConstants.onlyNumeric.test(input)) { onFieldChange("part_tsn_installation", input.slice(0, 10)); }
											else { e.preventDefault(); }
										}}
									/>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<TextField
										variant="outlined"
										label="Part CSN Installation"
										name="part_csn_installation"
										id="part_csn_installation"
										value={data.part_csn_installation || ''}
										onChange={(e) => {
											const input = e.target.value;
											if (input.length <= 10 && regexConstants.onlyNumeric.test(input)) { onFieldChange("part_csn_installation", input); }
											else if (input.length > 10 && regexConstants.onlyNumeric.test(input)) { onFieldChange("part_csn_installation", input.slice(0, 10)); }
											else { e.preventDefault(); }
										}}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<TextField
										variant="outlined"
										label={match.params.assetType == 1 ? "Aircraft TSN Installation" : "Engine TSN Installation"}
										name="aircraft_tsn_installation"
										id="aircraft_tsn_installation"
										value={data.aircraft_tsn_installation || ''}
										onChange={(e) => {
											const input = e.target.value;
											if (input.length <= 10 && regexConstants.onlyNumeric.test(input)) { onFieldChange("aircraft_tsn_installation", input); onRestErrorKey("aircraft_tsn_installation"); }
											else if (input.length > 10 && regexConstants.onlyNumeric.test(input)) { onFieldChange("aircraft_tsn_installation", input.slice(0, 10)); }
											else { e.preventDefault(); }
										}}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<TextField
										variant="outlined"
										label={match.params.assetType == 1 ? "Aircraft CSN Installation" : "Engine CSN Installation"}
										name="aircraft_csn_installation"
										id="aircraft_csn_installation"
										value={data.aircraft_csn_installation || ''}
										onChange={(e) => {
											const input = e.target.value;
											if (input.length <= 10 && regexConstants.onlyNumeric.test(input)) { onFieldChange("aircraft_csn_installation", input); onRestErrorKey("aircraft_csn_installation"); }
											else if (input.length > 10 && regexConstants.onlyNumeric.test(input)) { onFieldChange("aircraft_csn_installation", input.slice(0, 10)); }
											else { e.preventDefault(); }
										}}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>

								<Grid item md={3} sm={6} xs={12}>
									<TextField
										variant="outlined"
										required={acumenCamo ? false : (data.dimension_type === "FH" || data.dimension_type === "D" || data.dimension_type === "FH,D" || data.dimension_type === "FC,D") ? true : false}
										label="TSO"
										name="tso"
										id="tso"
										autoComplete="off"
										value={data.tso || ''}
										onChange={(e) => { onFieldChange('tso', e.target.value); onRestErrorKey("tso"); }}
										InputLabelProps={{ shrink: true }}
										error={!!error.tso}
										helperText={error.tso || ""}
										onFocus={() => onRestErrorKey("tso")}
										inputProps={{ maxLength: 10 }}
									/>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<TextField
										variant="outlined"
										required={acumenCamo ? false : (data.dimension_type === "FC" || data.dimension_type === "FC,D") ? true : false}
										label="CSO"
										name="cso"
										id="cso"
										value={data.cso || ''}
										inputProps={{ maxLength: 10 }}
										onChange={(e) => { onFieldChange('cso', e.target.value); onRestErrorKey("cso"); }}
										error={!!error.cso}
										helperText={error.cso || ""}
										onFocus={() => onRestErrorKey("cso")}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
								<Grid item md={3} sm={6} xs={12} className="input-calender">
									<MuiPickersUtilsProvider utils={MomentUtils}>
										<KeyboardDatePicker
											id="crs_date"
											required={acumenCamo ? false : true}
											label="CRS Date"
											format={acumenCamo ? fieldDMYShort : dayMonthDateFormat}
											inputVariant="outlined"
											fullWidth
											disableFuture
											InputLabelProps={{ shrink: true }}
											value={data?.crs_date || null}
											error={!!error.crs_date}
											helperText={error.crs_date || ""}
											onChange={(date) => { onFieldChange("crs_date", moment(date).isValid ? moment(date).format(backendDateFormat) : null); onRestErrorKey("crs_date"); }}
										/>
									</MuiPickersUtilsProvider>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<TextField
										variant="outlined"
										required={acumenCamo ? false : true}
										label="Position"
										name="position"
										id="position"
										value={data.position || ''}
										onChange={(e) => { onFieldChange("position", e.target.value); onRestErrorKey("position"); }}
										error={!!error.position}
										helperText={error.position || ""}
										onFocus={() => onRestErrorKey("position")}
										InputLabelProps={{ shrink: true }}
										inputProps={{ maxLength: '20' }}
									/>
								</Grid>
								<Grid item md={6} sm={6} xs={12}>
									<TextField
										required={acumenCamo ? false : true}
										variant="outlined"
										label="HT Requirement"
										name="ht_requirement"
										id="ht_requirement"
										value={data.ht_requirement || ''}
										onChange={(e) => { onFieldChange("ht_requirement", e.target.value.slice(0, 25)); onRestErrorKey("ht_requirement"); }}
										InputLabelProps={{ shrink: true }}
										error={!!error.ht_requirement}
										helperText={acumenCamo ? " " : error.ht_requirement || ""}
										onFocus={() => onRestErrorKey("ht_requirement")}
									/>
								</Grid>
							</Grid>
							<div className="border-grey"></div>
							<Grid id="data-container" spacing={1} container>
								<Grid item md={3} sm={12} xs={12}>
									<label>INTERVAL</label>
									<Grid spacing={2} container id="ht-dropdown">
										<Grid item>
											<FormControl>
												<InputLabel id="demo-simple-select-label">Dimension</InputLabel>
												<Select
													labelId="demo-simple-select-label"
													id="demo-simple-select"
													variant="outlined"
													value={data?.dimension_type}
													onChange={(e) => onFieldChange("dimension_type", e.target.value)}
												>
													<MenuItem value={"D"}>Days</MenuItem>
													<MenuItem value={"FH"}>FH</MenuItem>
													<MenuItem value={"FC"}>FC</MenuItem>
													<MenuItem value={"FC,D"}>FC & D</MenuItem>
													<MenuItem value={"FH,D"}>FH & D</MenuItem>
													<MenuItem value={"FC,FH,D"}>FC,FH & D</MenuItem>
													<MenuItem value={"FC,FH"}>FC & FH</MenuItem>
												</Select>
											</FormControl>
										</Grid>
									</Grid>
								</Grid>
								<Grid id="dimensions-input-ht" item md={3} sm={12} xs={12}>
									<Grid spacing={2} container>
										{(data.dimension_type === "FC" || data.dimension_type === "FC,D" || data.dimension_type === "FC,FH" || data.dimension_type === "FC,FH,D") && (
											<Grid item md={data.dimension_type === "FC" ? 12 : data.dimension_type === "FC,FH,D" ? 4 : 6} sm={4} xs={12}>
												<TextField
													variant="outlined"
													required={acumenCamo ? false : true}
													label="FC"
													name="dimension_interval_fc"
													id="dimension_interval_fc"
													value={data.dimension_interval_fc || ''}
													InputLabelProps={{ shrink: true }}
													onChange={(e) => {
														const input = e.target.value;
														if (input.length <= 5 && regexConstants.onlyNumeric.test(input)) { onFieldChange("dimension_interval_fc", input); onRestErrorKey("dimension_interval_fc"); }
														else if (input.length > 5 && regexConstants.onlyNumeric.test(input)) { onFieldChange("dimension_interval_fc", input.slice(0, 5)); setError(prev => ({ ...prev, dimension_interval_fc: "Please enter a 5 digit integer value." })); }
														else { e.preventDefault(); setError(prev => ({ ...prev, dimension_interval_fc: "Please enter a 5 digit integer value." })); }
													}}
													autoComplete="off"
													error={!!error.dimension_interval_fc}
													helperText={error.dimension_interval_fc || ""}
													onFocus={() => onRestErrorKey("dimension_interval_fc")}
												/>
											</Grid>
										)}
										{(data.dimension_type === "FH" || data.dimension_type === "FH,D" || data.dimension_type === "FC,FH" || data.dimension_type === "FC,FH,D") && (
											<Grid item xs={12} sm={4} md={data.dimension_type === "FH" ? 12 : data.dimension_type === "FC,FH,D" ? 4 : 6}>
												<TextField
													variant="outlined"
													required={acumenCamo ? false : true}
													label="FH"
													name="dimension_interval"
													id="dimension_interval"
													value={data.dimension_interval || ''}
													InputLabelProps={{ shrink: true }}
													onChange={(e) => {
														const input = e.target.value;
														if (input.length <= 5 && regexConstants.onlyNumeric.test(input)) { onFieldChange("dimension_interval", input); onRestErrorKey("dimension_interval"); }
														else if (input.length > 5 && regexConstants.onlyNumeric.test(input)) { onFieldChange("dimension_interval", input.slice(0, 5)); setError(prev => ({ ...prev, dimension_interval: "Please enter a 5 digit integer value." })); }
														else { e.preventDefault(); setError(prev => ({ ...prev, dimension_interval: "Please enter a 5 digit integer value." })); }
													}}
													autoComplete="off"
													error={!!error.dimension_interval}
													helperText={error.dimension_interval || ""}
													onFocus={() => onRestErrorKey("dimension_interval")}
												/>
											</Grid>
										)}
										{(data.dimension_type === "D" || data.dimension_type === "FC,D" || data.dimension_type === "FC,FH,D" || data.dimension_type === "FH,D") && (
											<Grid item xs={12} sm={4} md={data.dimension_type === "D" ? 12 : data.dimension_type === "FC,FH,D" ? 4 : 6}>
												<TextField
													variant="outlined"
													required={acumenCamo ? false : true}
													label="Days"
													name="dimension_interval_days"
													InputLabelProps={{ shrink: true }}
													id="dimension_interval_days"
													value={data?.dimension_interval_days || ''}
													onChange={(e) => {
														const input = e.target.value;
														if (input.length <= 5 && regexConstants.onlyNumeric.test(input)) { onFieldChange("dimension_interval_days", input); onRestErrorKey("dimension_interval_days"); }
														else if (input.length > 5 && regexConstants.onlyNumeric.test(input)) { onFieldChange("dimension_interval_days", input.slice(0, 5)); setError(prev => ({ ...prev, dimension_interval_days: "Please enter a 5 digit integer value." })); }
														else { e.preventDefault(); setError(prev => ({ ...prev, dimension_interval_days: "Please enter a 5 digit integer value." })); }
													}}
													autoComplete="off"
													error={!!error.dimension_interval_days}
													helperText={error.dimension_interval_days || ""}
													onFocus={() => onRestErrorKey("dimension_interval_days")}
												/>
											</Grid>
										)}
									</Grid>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<label>REMAINING</label>
									<Grid spacing={1} container>
										<Grid className="remaining-input" item md={12} sm={12} xs={12}>
											<TextField
												variant="outlined"
												label={dimensionLabel(data.dimension_type)}
												name="remaining_val"
												id="remaining_val"
												value={computeRemainingVal(data, headerTsn, headerCsn) ?? ''}
												disabled
												className="disabled-input"
												InputLabelProps={{ shrink: true }}
											/>
										</Grid>
									</Grid>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<label>DUE</label>
									<Grid spacing={2} container>
										<Grid className="remaining-input" item md={6} sm={6} xs={6}>
											<TextField
												variant="outlined"
												label={dimensionLabel(data.dimension_type).replace("Days", "Date").replace("FH/Days", "FH/Date").replace("FC/Days", "FC/Date").replace("FC/FH/Days", "FC/FH/Date")}
												name="ht_DUE"
												id="ht_DUE"
												value={computeNextDueVal(data, headerTsn, headerCsn) ?? ''}
												InputLabelProps={{ shrink: true }}
												disabled
												className="disabled-input"
											/>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
							<Grid id="data-container" spacing={1} container>
								<Grid item md={2} sm={6} xs={12}>
									<label>FAA / EASA Certificate{acumenCamo ? null : <span style={{ color: "red" }}> *</span>}</label>
									<FormControl error={!!error.faa_easa} onFocus={() => onRestErrorKey("faa_easa")}>
										<RadioGroup row aria-label="faa_easa" name="faa_easa" value={data.faa_easa} onChange={(e) => onFieldChange("faa_easa", e.target.value)}>
											<FormControlLabel value="yes" control={<Radio size="small" color="primary" />} label="Yes" />
											<FormControlLabel value="no" control={<Radio size="small" color="primary" />} label="No" />
										</RadioGroup>
										{error.faa_easa && <FormLabel component="faa_easa"><span style={{ color: "red" }}>{error.faa_easa}</span></FormLabel>}
									</FormControl>
								</Grid>
								<Grid item md={2} sm={6} xs={12}>
									<label>Shop Report</label>
									<FormControl component="fieldset">
										<RadioGroup row aria-label="shop_report" name="shop_report" value={data.shop_report} onChange={(e) => onFieldChange("shop_report", e.target.value)}>
											<FormControlLabel value="yes" control={<Radio size="small" color="primary" />} label="Yes" />
											<FormControlLabel value="no" control={<Radio size="small" color="primary" />} label="No" />
										</RadioGroup>
									</FormControl>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<label>Certificate of Conformity</label>
									<FormControl component="fieldset">
										<RadioGroup row aria-label="conformity_crt" name="conformity_crt" value={data.conformity_crt} onChange={(e) => onFieldChange("conformity_crt", e.target.value)}>
											<FormControlLabel value="yes" control={<Radio size="small" color="primary" />} label="Yes" />
											<FormControlLabel value="no" control={<Radio size="small" color="primary" />} label="No" />
											<FormControlLabel value="N/A" control={<Radio size="small" color="primary" />} label="N/A" />
										</RadioGroup>
									</FormControl>
								</Grid>
								<Grid item md={2} sm={6} xs={12}>
									<label>Goods receipt note</label>
									<FormControl component="fieldset">
										<RadioGroup row aria-label="goods_rec_note" name="goods_rec_note" value={data.goods_rec_note} onChange={(e) => onFieldChange("goods_rec_note", e.target.value)}>
											<FormControlLabel value="yes" control={<Radio size="small" color="primary" />} label="Yes" />
											<FormControlLabel value="no" control={<Radio size="small" color="primary" />} label="No" />
										</RadioGroup>
									</FormControl>
								</Grid>
								<Grid item md={3} sm={6} xs={12}>
									<label>Installation Work order{acumenCamo ? null : <span style={{ color: "red" }}> *</span>}</label>
									<FormControl error={!!error.installation_wo} onFocus={() => onRestErrorKey("installation_wo")}>
										<RadioGroup row aria-label="installation_wo" name="installation_wo" value={data.installation_wo} onChange={(e) => onFieldChange("installation_wo", e.target.value)}>
											<FormControlLabel value="yes" control={<Radio size="small" color="primary" />} label="Yes" />
											<FormControlLabel value="no" control={<Radio size="small" color="primary" />} label="No" />
											<FormControlLabel value="N/A" control={<Radio size="small" color="primary" />} label="N/A" />
										</RadioGroup>
										{error.installation_wo && <FormLabel component="installation_wo"><span style={{ color: "red" }}>{error.installation_wo}</span></FormLabel>}
									</FormControl>
								</Grid>
							</Grid>
							<Grid spacing={1} container>
								<Grid item md={6} sm={12} xs={12}>
									<TextField
										label="Remark"
										margin='normal'
										variant='outlined'
										fullWidth
										value={data.remark || ''}
										InputLabelProps={{ shrink: true }}
										onChange={(e) => onFieldChange("remark", e.target.value)}
										rows={3}
										multiline
										inputProps={{ maxLength: 255 }}
									/>
								</Grid>
								<Grid item md={6} sm={12} xs={12}>
									<div className="ht-form__file-upload-box">
										<ul className="list-inline ht-form__upload-list">
											<li className="list-inline-item" onClick={() => setUploadFile(true)}>
												<span className="flex-centered"><AttachmentIcon color='primary' fontSize='small' /> Upload File</span>
											</li>
											<li className="list-inline-item" onClick={() => setLinkFile(true)}>
												<span className="flex-centered"><LinkIcon color='primary' fontSize='small' />Link file from Records Module</span>
											</li>
										</ul>
									</div>
								</Grid>
							</Grid>
							<Grid id="data-container" spacing={1} container>
								<Grid spacing={1} container>
									{modeType === 'add' ? (
										<div className='checklist-files'>
											<ul className='list-inline'>
												{data?.checklist?.map((file, i) => (
													<li key={i} className='list-inline-item'>
														<span className='file-name'>{file.name}</span>
														<span className='file-remove'>
															<a href={decodeURIComponent(file.path)} download={file.name}><GetAppIcon color="primary" /></a>
															<CloseIcon onClick={() => onRemoveFile(file)} color='secondary' fontSize='small' />
														</span>
													</li>
												))}
												{attachments?.map((attachment, i) => (
													<li key={i} className='list-inline-item'>
														<span className='file-name'>{attachment.file_name}</span>
														<span className='file-remove'>
															<a href={decodeURIComponent(attachment.file)} download={attachment.file_name}><GetAppIcon color="primary" /></a>
															<CloseIcon onClick={() => deleteAttachmentFile(0, attachment)} color='secondary' fontSize='small' />
														</span>
													</li>
												))}
											</ul>
										</div>
									) : (
										<div className='checklist-files'>
											<ul className='list-inline'>
												{data?.checklist?.files?.map((file, i) => (
													<li key={i} className='list-inline-item'>
														<span className='file-name'>{file.name}</span>
														<span className='file-remove'>
															<a href={decodeURIComponent(file.path)} download={file.name}><GetAppIcon color="primary" /></a>
															<CloseIcon onClick={() => onUnlinkCheckListFile(data.checklist, file.id)} color='secondary' fontSize='small' />
														</span>
													</li>
												))}
												{data.checklist?.folders?.length > 0 && data.checklist.folders.map((file) => (
													<li className='list-inline-item' key={file.id}>
														<Tooltip title={`Records Data Room :- ${file?.location}`} arrow>
															<a href={`${config.domain.records}${assetTypeMap[match?.params?.assetType]}/${file?.asset_slug}/data-room/technical/${file?.uuid}`} target="_new">
																<img style={{ width: '15px', float: 'right', marginRight: '-18px', marginTop: '7px' }} src={imgStoragePath + 'phase-2/RecordsIcon.svg'} alt="Records Data Room" />
															</a>
														</Tooltip>
														<span className='file-name'>{file.name}</span>
														<span className='file-remove'>
															<CloseIcon onClick={() => onUnlinkCheckListFile(data.checklist, file.id)} color='secondary' fontSize='small' />
														</span>
													</li>
												))}
												{attachments?.map((attachment, i) => (
													<li key={i} className='list-inline-item'>
														<span className='file-name'>{attachment.file_name}</span>
														<span className='file-remove'>
															<a href={decodeURIComponent(attachment.file)} download={attachment.file_name}><GetAppIcon color="primary" /></a>
															<CloseIcon onClick={() => deleteAttachmentFile(attachment.id, attachment)} color='secondary' fontSize='small' />
														</span>
													</li>
												))}
											</ul>
										</div>
									)}
								</Grid>
							</Grid>
						</DialogContent>
						<DialogActions className="ht-form__actions">
							<Button type="button" className="btn btn-primary" onClick={modeType === "add" ? addFormData : handleFormSubmit}>
								{modeType === "add" ? "Add" : "Update"}
							</Button>
							{modeType === "add" && (
								<Button onClick={saveAndAddAnother} type="button" className="btn btn-primary">Save & Add Another</Button>
							)}
							<Button type="button" className="btn btn-primary" onClick={modalCloseFn}>Close</Button>
						</DialogActions>
						<Dialog open={linkFile} onClose={() => setLinkFile(false)} id="htDialog" style={{ height: isMobile && "650px" }}>
							<LinkFiles
								handleClose={() => setLinkFile(false)}
								uuid={uuid?.uuid}
								editFormId={htData?.id}
								getResponseBack={(file) => getResponseBackFn(file)}
								listResponseBack={(file) => listResponseBack(file)}
								actionType={modeType}
								onLinkFile={(file, any, type) => onLinkFile(file, any, type)}
								modType="ht"
								LinkFileInfo={data}
								last_used_folder_uuid={last_used_folder_uuid_link_document}
								uploadFileInfo={data}
								attachments={attachments}
								type="link"
							/>
						</Dialog>
						<Dialog open={uploadFile} onClose={() => setUploadFile(false)} id="htDialog" style={{ height: isMobile && "650px" }}>
							<UploadFile
								handleClose={() => setUploadFile(false)}
								uuid={uuid?.uuid}
								editFormId={htData?.id}
								getResponseBack={(file) => getResponseBackFn(file)}
								listResponseBack={(file) => listResponseBack(file)}
								actionType={modeType}
								onLinkFile={(file, uuidVal) => onLinkFile(file, uuidVal)}
								modType="ht"
								uploadFileInfo={data}
								last_used_folder_uuid={last_used_folder_uuid}
								onUpload={(file) => onUpload(file)}
								type="upload"
								checkbox_last_used_folder_uuid={checkbox_last_used_folder_uuid}
								checkbox_last_used_folder_name={checkbox_last_used_folder_name}
							/>
						</Dialog>
					</form>
				</div>
			</Dialog>
		</>
	);
};

export default withRouter(HtTaskForm);
